/**
 * "Continue where you left off?"
 *
 * A tab reloads, iPadOS evicts the page under memory pressure, someone closes the lid.
 * None of those are events the player caused, and none of them should cost them a
 * session. The card shows the last four chords rather than a timestamp or a session id,
 * because that is the only description of a doodle that means anything to the person who
 * made it — you recognise your own progression instantly and a UUID never.
 *
 * Both answers are one tap and neither is destructive-looking. Starting fresh is not
 * "discard"; the snapshot stays in the sketchbook either way.
 */

import { motion } from 'framer-motion';
import type { SessionState } from '@hc/shared';
import { ChordSymbol } from './ChordSymbol.js';
import { useCalmMotion } from './motionPreference.js';
import { CALM_FADE } from '../graph/motion.js';

/** As many chords as read as a phrase at a glance. More becomes a list. */
const PREVIEW_LENGTH = 4;

export interface ResumeCardProps {
  snapshot: SessionState;
  onResume: () => void;
  onStartFresh: () => void;
}

export function ResumeCard({
  snapshot,
  onResume,
  onStartFresh,
}: ResumeCardProps): React.JSX.Element {
  const calm = useCalmMotion();
  const preview = snapshot.path.slice(-PREVIEW_LENGTH);

  return (
    <motion.div
      className="hc-resume"
      data-testid="hc-resume-card"
      role="group"
      aria-label="Continue where you left off?"
      // Transform-only, for the same reason as the home screen: this card asks a question
      // that blocks the session, and it must be readable even when the document timeline
      // is paused and no animation ever runs.
      initial={calm ? false : { y: 12 }}
      animate={{ y: 0 }}
      transition={calm ? CALM_FADE : { type: 'spring', stiffness: 340, damping: 34 }}
    >
      <p className="hc-resume__question">Continue where you left off?</p>
      <ol className="hc-resume__path" data-testid="hc-resume-path">
        {preview.map((node) => (
          <li key={node.id} className="hc-resume__chord">
            <ChordSymbol symbol={node.chord.symbol} />
          </li>
        ))}
        {preview.length === 0 ? <li className="hc-resume__chord">—</li> : null}
      </ol>
      {snapshot.intention ? <p className="hc-resume__intention">“{snapshot.intention}”</p> : null}
      <div className="hc-resume__actions">
        <button type="button" className="hc-button hc-button--primary" onClick={onResume}>
          Continue
        </button>
        <button type="button" className="hc-button hc-button--quiet" onClick={onStartFresh}>
          Start fresh
        </button>
      </div>
    </motion.div>
  );
}
